import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import VideoEmbed from './VideoEmbed';
import ContentReportModal from './ContentReportModal';

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Custom hook for intersection observer
const useIntersectionObserver = (ref, options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      console.log('👁️ INTERSECTION:', entry.isIntersecting, 'Ratio:', entry.intersectionRatio);
      setIsIntersecting(entry.isIntersecting);
    }, {
      threshold: 0.01, // Lower threshold - trigger when 1% of feed is visible
      ...options
    });
    
    observer.observe(element);
    
    return () => {
      observer.disconnect();
    };
  }, [ref, options]);
  
  return isIntersecting;
};

const AlumniNewsFeed = ({ currentUser, onError, newPostData }) => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [newPostIds, setNewPostIds] = useState(new Set());
  const [queuedUpdates, setQueuedUpdates] = useState([]);
  const [newestTimestamp, setNewestTimestamp] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [lastRefreshReason, setLastRefreshReason] = useState('');
  
  // Refs and visibility detection
  const feedRef = useRef(null);
  const isVisible = useIntersectionObserver(feedRef);
  
  // Debug logging for visibility changes
  useEffect(() => {
    console.log('🔍 FEED VISIBILITY:', isVisible ? 'VISIBLE' : 'HIDDEN');
    console.log('🔍 FEED REF:', feedRef.current ? 'HAS ELEMENT' : 'NO ELEMENT');
  }, [isVisible]);
  
  // Create debounced refresh function  
  const debouncedRefresh = useMemo(
    () => debounce((reason = 'unknown') => {
      console.log('🔄 EXECUTING REFRESH');
      setRefreshCount(prev => prev + 1);
      setLastRefreshReason(reason);
      // Use incremental update if we have a timestamp, otherwise full refresh
      if (newestTimestamp) {
        fetchIncrementalPosts();
      } else {
        fetchPosts(0);
      }
    }, 1000), // Wait 1 second between rapid updates
    [newestTimestamp]
  );

  useEffect(() => {
    console.log('🏁 INITIAL LOAD - calling fetchPosts');
    setRefreshCount(prev => prev + 1);
    setLastRefreshReason('initial-load');
    fetchPosts();
  }, []);

  // Listen for refresh events from parent components
  useEffect(() => {
    const handleRefreshEvent = () => {
      console.log('🔄 REFRESH EVENT received, fetching new posts');
      setRefreshCount(prev => prev + 1);
      setLastRefreshReason('refresh-event');
      fetchPosts(0); // Fetch from the beginning
    };

    window.addEventListener('refreshFeed', handleRefreshEvent);
    
    return () => {
      window.removeEventListener('refreshFeed', handleRefreshEvent);
    };
  }, []);

  // Handle new post data from WebSocket (optimized with visibility)
  useEffect(() => {
    if (newPostData && newPostData.id) {
      console.log('New post detected via WebSocket:', newPostData);
      
      const newPostId = newPostData.id;
      
      // Always mark for animation (so it shows when feed becomes visible)
      setNewPostIds(prevIds => new Set([...prevIds, newPostId]));
      
      if (isVisible) {
        console.log('📡 WEBSOCKET: Feed visible, using WebSocket data directly');
        // Use the WebSocket data directly instead of fetching from API
        if (newPostData.author_name && newPostData.content !== undefined) {
          // Create a properly formatted post object from WebSocket data
          const newPost = {
            id: newPostData.id,
            author_id: newPostData.author_id,
            author_name: newPostData.author_name,
            author_headshot_url: null, // WebSocket doesn't include this
            content: newPostData.content,
            post_type: newPostData.post_type || 'status',
            media_url: newPostData.media_url,
            media_type: newPostData.media_type,
            visibility: newPostData.visibility || 'network',
            created_at: newPostData.created_at,
            updated_at: newPostData.created_at,
            like_count: 0,
            comment_count: 0,
            share_count: 0,
            user_liked: false,
            user_shared: false,
            recent_interactions: []
          };
          
          // Add the new post to the TOP of the feed
          setPosts(prevPosts => {
            // Check if post already exists to avoid duplicates
            const existsAlready = prevPosts.some(post => post.id === newPost.id);
            if (existsAlready) {
              console.log('Post already exists in feed, skipping add');
              return prevPosts;
            }
            console.log('Adding new WebSocket post to feed with media_type:', newPost.media_type);
            return [newPost, ...prevPosts];
          });
        } else {
          // Fallback to API refresh if WebSocket data is incomplete
          console.log('WebSocket data incomplete, falling back to API refresh');
          debouncedRefresh('websocket');
        }
      } else {
        console.log('WebSocket: Feed not visible, queueing update');
        setQueuedUpdates(prev => [
          ...prev.filter(update => update.id !== newPostId), // Remove duplicate
          { id: newPostId, timestamp: Date.now() }
        ]);
      }
      
      // Remove the new post highlight after 3 seconds
      setTimeout(() => {
        setNewPostIds(prevIds => {
          const newIds = new Set(prevIds);
          newIds.delete(newPostId);
          return newIds;
        });
      }, 3000);
    }
  }, [newPostData, isVisible, debouncedRefresh]);

  // Process queued updates when feed becomes visible
  useEffect(() => {
    if (isVisible && queuedUpdates.length > 0) {
      console.log(`📥 QUEUE: Processing ${queuedUpdates.length} queued updates`);
      
      // Clear the queue and refresh feed
      setQueuedUpdates([]);
      debouncedRefresh('queue-processing');
    }
  }, [isVisible, queuedUpdates, debouncedRefresh]);

  const fetchCompletePostData = async (postId) => {
    try {
      console.log('Fetching complete post data for ID:', postId);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/alumni/posts/${postId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Fetch response status:', response.status);

      if (response.ok) {
        const completePost = await response.json();
        console.log('Complete post data received:', completePost);
        
        // Add the new post to the TOP of the feed
        setPosts(prevPosts => {
          console.log('Current posts count:', prevPosts.length);
          // Check if post already exists to avoid duplicates
          const existsAlready = prevPosts.some(post => post.id === completePost.id);
          if (existsAlready) {
            console.log('Post already exists, skipping add');
            return prevPosts;
          }
          console.log('Adding new post to feed');
          return [completePost, ...prevPosts];
        });
        
        // Mark this post as new for animation
        setNewPostIds(prevIds => new Set([...prevIds, completePost.id]));
        
        // Remove the new post highlight after 3 seconds
        setTimeout(() => {
          setNewPostIds(prevIds => {
            const newIds = new Set(prevIds);
            newIds.delete(completePost.id);
            return newIds;
          });
        }, 3000);
      } else {
        console.error('Failed to fetch post:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching complete post data:', error);
      // Fallback: just refresh the entire feed
      fetchPosts(0);
    }
  };

  const fetchIncrementalPosts = async () => {
    console.log('📈 FETCHINCREMENTALPOSTS called');
    setRefreshCount(prev => prev + 1);
    setLastRefreshReason('incremental-posts');
    
    if (!newestTimestamp) {
      // console.log('No timestamp for incremental update, falling back to full refresh');
      setLastRefreshReason('incremental-fallback');
      return fetchPosts(0);
    }
    
    try {
      // console.log('Fetching incremental posts since:', newestTimestamp);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${backendUrl}/api/alumni/feed/incremental?since=${encodeURIComponent(newestTimestamp)}&limit=20`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load incremental posts');
      }

      if (data.posts && data.posts.length > 0) {
        // console.log(`Got ${data.posts.length} new posts from incremental update`);
        
        // Update newest timestamp
        setNewestTimestamp(data.newest_timestamp);
        
        // Prepend new posts to existing posts (avoiding duplicates)
        setPosts(prevPosts => {
          const existingIds = new Set(prevPosts.map(p => p.id));
          const uniqueNewPosts = data.posts.filter(post => !existingIds.has(post.id));
          
          if (uniqueNewPosts.length > 0) {
            // Mark new posts for animation
            const newIds = new Set(uniqueNewPosts.map(p => p.id));
            setNewPostIds(prevIds => new Set([...prevIds, ...newIds]));
            
            // Remove animation after 3 seconds
            setTimeout(() => {
              setNewPostIds(prevIds => {
                const updatedIds = new Set(prevIds);
                newIds.forEach(id => updatedIds.delete(id));
                return updatedIds;
              });
            }, 3000);
          }
          
          return [...uniqueNewPosts, ...prevPosts];
        });
      } else {
        // console.log('No new posts in incremental update');
      }
      
      setError('');
      
    } catch (err) {
      console.error('Error fetching incremental posts:', err);
      // Fallback to full refresh on error
      console.log('Falling back to full refresh due to error');
      fetchPosts(0);
    }
  };

  const fetchPosts = async (skip = 0) => {
    try {
      console.log('📄 FETCHPOSTS called with skip:', skip);
      // Don't override the refresh reason here - let the caller set it
      
      if (skip === 0) setIsLoading(true);
      else setIsLoadingMore(true);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/alumni/feed?skip=${skip}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load posts');
      }

      if (skip === 0) {
        setPosts(data.posts);
        // Clear new post animations when refreshing the feed
        setNewPostIds(new Set());
        // Track the newest post timestamp for incremental updates
        if (data.posts && data.posts.length > 0) {
          setNewestTimestamp(data.posts[0].created_at);
        }
      } else {
        setPosts(prev => [...prev, ...data.posts]);
      }
      
      setHasMore(data.has_more);
      setError('');

    } catch (err) {
      console.error('Error fetching posts:', err);
      const errorMessage = err.message || 'Failed to load posts. Please try again.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleInteraction = async (postId, interactionType, content = null) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/alumni/posts/${postId}/interact`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          interaction_type: interactionType,
          content: content
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to interact with post');
      }

      // Refresh the specific post data
      await fetchPosts(0);

    } catch (err) {
      console.error('Error interacting with post:', err);
      setError(err.message || 'Failed to interact with post');
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchPosts(posts.length);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    return date.toLocaleDateString();
  };

  const getPostTypeIcon = (postType) => {
    const icons = {
      status: '💭',
      job_update: '💼',
      achievement: '🏆',
      article_share: '📰'
    };
    return icons[postType] || '💭';
  };

  const getVisibilityIcon = (visibility) => {
    const icons = {
      public: '🌐',
      network: '👥',
      private: '🔒'
    };
    return icons[visibility] || '👥';
  };

  const PostCard = ({ post, isNewPost = false }) => {
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportData, setReportData] = useState({
      contentType: '',
      contentId: null,
      reportedUserId: null,
      contentPreview: ''
    });

    const handleComment = async (e) => {
      e.preventDefault();
      if (!commentText.trim()) return;

      setIsSubmittingComment(true);
      await handleInteraction(post.id, 'comment', commentText.trim());
      setCommentText('');
      setIsSubmittingComment(false);
    };

    const handleDeletePost = async () => {
      try {
        setIsDeleting(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const token = localStorage.getItem('token');

        const response = await fetch(`${backendUrl}/api/alumni/posts/${post.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          console.log('Post deleted successfully');
          // Refresh the feed to remove the deleted post
          fetchPosts(0);
          setShowDeleteConfirm(false);
        } else {
          const errorData = await response.json();
          console.error('Failed to delete post:', errorData.detail);
          setError(errorData.detail || 'Failed to delete post');
        }
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('Failed to delete post. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    };

    const handleReportContent = (contentType, contentId, reportedUserId, contentPreview) => {
      setReportData({
        contentType,
        contentId,
        reportedUserId,
        contentPreview
      });
      setShowReportModal(true);
    };

    return (
      <div className={`bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 transition-all duration-500 ${
        isNewPost 
          ? 'animate-pulse border-l-4 border-blue-500 bg-blue-50' 
          : ''
      }`}>
        {/* Post Header */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="flex-shrink-0 w-10 sm:w-12">
            {post.author_headshot_url ? (
              <img
                src={post.author_headshot_url}
                alt=""
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-xs sm:text-sm ${post.author_headshot_url ? 'hidden' : 'flex'}`}
            >
              {post.author_name ? post.author_name.split(' ').map(n => n[0]).join('').toUpperCase() : '?'}
            </div>
          </div>
          
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                  {post.author_name}
                </h3>
                <span className="text-lg flex-shrink-0" title={post.post_type}>
                  {getPostTypeIcon(post.post_type)}
                </span>
                <span className="text-sm flex-shrink-0" title={`Visible to: ${post.visibility}`}>
                  {getVisibilityIcon(post.visibility)}
                </span>
              </div>
              
              {/* Action buttons */}
              <div className="flex-shrink-0 flex items-center space-x-2">
                {/* Report button - only show for posts NOT owned by current user */}
                {currentUser && post.author_id !== currentUser.id && (
                  <button
                    onClick={() => handleReportContent('post', post.id, post.author_id, `${post.author_name}: ${post.content.substring(0, 100)}...`)}
                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                    title="Report post"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.312 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </button>
                )}

                {/* Delete button - only show for posts owned by current user */}
                {currentUser && post.author_id === currentUser.id && (
                  <div>
                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1"
                        title="Delete post"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2 text-xs">
                        <button
                          onClick={handleDeletePost}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                        >
                          {isDeleting ? 'Deleting...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <time dateTime={post.created_at}>
                {formatTimeAgo(post.created_at)}
              </time>
              {post.updated_at !== post.created_at && (
                <span>• edited</span>
              )}
            </div>
          </div>
        </div>

        {/* Post Content */}
        <div className="mb-4">
          <p className="text-gray-900 text-sm sm:text-base whitespace-pre-wrap">
            {post.content}
          </p>
          
          {/* Media Content */}
          {post.media_url && (
            <div className="mt-3">
              {post.media_type === 'image' ? (
                <div className="rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={post.media_url}
                    alt="Post attachment"
                    className="w-full max-h-96 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div 
                    className="hidden p-4 text-center text-gray-500 text-sm"
                  >
                    Image could not be loaded
                  </div>
                </div>
              ) : (
                <VideoEmbed url={post.media_url} className="" />
              )}
            </div>
          )}
        </div>

        {/* Interaction Bar */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => handleInteraction(post.id, 'like')}
              className={`flex items-center space-x-1 text-sm ${
                post.user_liked ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
              } transition-colors`}
            >
              <span>{post.user_liked ? '👍' : '👍'}</span>
              <span>{post.like_count}</span>
            </button>

            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              <span>💬</span>
              <span>{post.comment_count}</span>
            </button>

            <button
              onClick={() => handleInteraction(post.id, 'share')}
              className={`flex items-center space-x-1 text-sm ${
                post.user_shared ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
              } transition-colors`}
            >
              <span>🔄</span>
              <span>{post.share_count}</span>
            </button>
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {/* Recent Comments */}
            {post.recent_interactions
              .filter(interaction => interaction.interaction_type === 'comment')
              .map(comment => (
                <div key={comment.id} className="mb-3 last:mb-0">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-white font-medium">
                        {comment.user_name.split(' ').map(n => n[0]).join('')}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-100 rounded-lg px-3 py-2">
                        <div className="text-xs font-medium text-gray-900 mb-1">
                          {comment.user_name}
                        </div>
                        <div className="text-sm text-gray-700">
                          {comment.content}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                        <time dateTime={comment.created_at}>
                          {formatTimeAgo(comment.created_at)}
                        </time>
                        {/* Report comment button - only show for comments NOT by current user */}
                        {currentUser && comment.user_id !== currentUser.id && (
                          <button
                            onClick={() => handleReportContent('comment', comment.id, comment.user_id, `${comment.user_name}: ${comment.content.substring(0, 50)}...`)}
                            className="text-gray-400 hover:text-red-600 transition-colors ml-2"
                            title="Report comment"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.312 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            {/* Comment Form */}
            <form onSubmit={handleComment} className="mt-3 flex items-start space-x-2">
              <div className="flex-shrink-0">
                {currentUser?.headshot_url ? (
                  <img
                    src={currentUser.headshot_url}
                    alt="Your avatar"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-xs text-white font-medium">
                    {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 text-sm border border-gray-300 rounded-full px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={500}
                />
                <button
                  type="submit"
                  disabled={!commentText.trim() || isSubmittingComment}
                  className={`px-3 py-2 rounded-full text-xs font-medium transition-colors self-end sm:self-auto ${
                    !commentText.trim() || isSubmittingComment
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isSubmittingComment ? '...' : 'Post'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Content Report Modal */}
        <ContentReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType={reportData.contentType}
          contentId={reportData.contentId}
          reportedUserId={reportData.reportedUserId}
          contentPreview={reportData.contentPreview}
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="flex items-start space-x-3 mb-4">
              <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/6"></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        <p className="font-medium">Error loading posts</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={() => fetchPosts(0)}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-gray-400 text-4xl mb-4">📝</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
        <p className="text-gray-600">
          Be the first to share an update with your network!
        </p>
      </div>
    );
  }

  return (
    <div ref={feedRef}>
      
      {/* Posts */}
      <div className="space-y-6">
        {posts.map(post => (
          <PostCard 
            key={post.id} 
            post={post} 
            isNewPost={newPostIds.has(post.id)}
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-6 text-center">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              isLoadingMore
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoadingMore ? 'Loading...' : 'Load More Posts'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AlumniNewsFeed;